import { inject, ChangeDetectionStrategy, Component, computed, input, OnChanges, OnInit, Signal, signal, SimpleChanges, viewChild, WritableSignal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';
import { CommonModule } from '@angular/common';
import { TableModule, TableRowSelectEvent } from 'primeng/table';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputMask } from 'primeng/inputmask';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { Dialog } from 'primeng/dialog';
import { CardContent } from '@/shared/components/card-content/card-content';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { HistoricoFeatureService } from './historico.service';
import { HistoricoRequest, MedicoHistoricoCancelamentoRequest } from '@/core/api/sicom-api/registro/medico/historico/historico.model';
import { finalize } from 'rxjs/operators';
import { MenuItem, MessageService } from 'primeng/api';
import { TabContainer, TabContainerOptionType, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';
import { DateUtils } from '@/shared/utils/date-utils';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { TagModule } from 'primeng/tag';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { dataParaTimestamp, EventoInscricao, MovimentacaoHistorico, paraEventosTimeline } from './historico.presenter';
import { TimelineInscricoes } from './timeline-inscricoes';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface SituacaoMedicoRow {
    dataReferencia: string;
    descricao: string;
    origemDestino: string;
    dataCancelamento?: string;
    motivoCancelamento?: string;
    idModoInscricao: number;
    ufSgUf: string;
}

interface InscricaoSecundariaRow {
    dataInscricao: string;
    ufDestino: string;
    modoInscricao: string;
    dataCancelamento?: string;
    idModoInscricao: number;
    ufSgUf: string;
}

interface MaisInformacaoItem {
    label: string;
    valor: string;
}

@Component({
    selector: 'app-historico',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        ReactiveFormsModule,
        TableModule,
        Button,
        InputMask,
        SelectEstado,
        Dialog,
        CardContent,
        BotaoAcoes,
        TableViewSwitcherComponent,
        TabContainer,
        TabContent,
        CustomTableComponent,
        CardContainer,
        TagModule,
        CanDirective,
        TimelineInscricoes
    ],
    templateUrl: './historico.html',
    styleUrl: './historico.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Aba de histórico: situação do médico e inscrições secundárias. */
export class Historico implements OnChanges, OnInit {
    private readonly logger = inject(LoggerService);
    tabelaSituacao = viewChild<CustomTableComponent>('tabelaSituacao');
    tabelaInscricao = viewChild<CustomTableComponent>('tabelaInscricao');

    pessoa = input<Pessoa>();
    protected readonly roleHistoricoCancelar = PERMISSOES.medicos.historico.cancelar;
    protected readonly roleHistoricoInscricaoSecundaria = PERMISSOES.medicos.historico.inscricaoSecundaria;

    situacaoMedico: WritableSignal<SituacaoMedicoRow[]> = signal<SituacaoMedicoRow[]>([]);
    historicoSecundaria: WritableSignal<InscricaoSecundariaRow[]> = signal<InscricaoSecundariaRow[]>([]);

    readonly eventosSituacao: Signal<EventoInscricao[]> = computed(() =>
        paraEventosTimeline(
            this.situacaoMedico().map(
                (linha, indice): MovimentacaoHistorico => ({
                    indiceLinha: indice,
                    idModoInscricao: linha.idModoInscricao,
                    descricao: linha.descricao,
                    ufMovimentacao: linha.origemDestino,
                    ufRegistro: linha.ufSgUf,
                    data: linha.dataReferencia,
                    dataCancelamento: linha.dataCancelamento,
                    motivoCancelamento: linha.motivoCancelamento,
                    rotuloUf: 'Origem/Destino'
                })
            )
        )
    );

    readonly eventosSecundaria: Signal<EventoInscricao[]> = computed(() =>
        paraEventosTimeline(
            this.historicoSecundaria().map(
                (linha, indice): MovimentacaoHistorico => ({
                    indiceLinha: indice,
                    idModoInscricao: linha.idModoInscricao,
                    descricao: linha.modoInscricao,
                    ufMovimentacao: linha.ufDestino,
                    ufRegistro: linha.ufSgUf,
                    data: linha.dataInscricao,
                    dataCancelamento: linha.dataCancelamento,
                    rotuloUf: 'UF Destino'
                })
            )
        )
    );

    situacaoTableMetadata: Signal<TableMetadata> = computed(() => {
        return {
            headers: {
                dataReferencia: { type: 'text', value: 'Data Referência', hidden: false, default: 'N/A' },
                descricao: { type: 'text', value: 'Descrição', hidden: false, default: 'N/A' },
                origemDestino: { type: 'text', value: 'Origem/Destino', hidden: false, default: 'N/A' },
                dataCancelamento: { type: 'text', value: 'Cancelamento', hidden: false, default: 'N/A' },
                motivoCancelamento: { type: 'text', value: 'Motivo Cancelamento', hidden: true, default: 'N/A' }
            },
            title: 'descricao',
            tag: '',
            badgeFields: ['dataReferencia'],
            actions: {
                cancelar: {
                    label: 'Cancelar Inscrição',
                    icon: 'pi pi-ban',
                    color: 'danger',
                    permission: PERMISSOES.medicos.historico.cancelar,
                    command: (item: SituacaoMedicoRow) => {
                        const idx = this.situacaoMedico().indexOf(item);
                        if (idx !== -1) {
                            this.onSituacaoRowClick(item);
                            this.enableSituacaoUpdateDelete({ data: item } as TableRowSelectEvent);
                            this.abrirDialogExcluirSituacaoWithIndex(idx);
                        }
                    }
                }
            }
        } as TableMetadata;
    });

    situacaoMedicoColumnOrder = ['dataReferencia', 'descricao', 'origemDestino', 'dataCancelamento', 'motivoCancelamento'];

    secundariaTableMetadata: Signal<TableMetadata> = computed(() => {
        return {
            headers: {
                dataInscricao: { type: 'text', value: 'Data Inscrição', hidden: false, default: '' },
                ufDestino: { type: 'text', value: 'UF Destino', hidden: false, default: '' },
                modoInscricao: { type: 'text', value: 'Modo Inscrição', hidden: false, default: '' },
                dataCancelamento: { type: 'text', value: 'Data Cancelamento', hidden: false, default: '' }
            },
            title: 'modoInscricao',
            tag: 'ufDestino',
            actions: {
                cancelar: {
                    label: 'Cancelar Inscrição',
                    icon: 'pi pi-ban',
                    color: 'danger',
                    permission: PERMISSOES.medicos.historico.cancelar,
                    command: (item: InscricaoSecundariaRow) => {
                        const idx = this.historicoSecundaria().indexOf(item);
                        if (idx !== -1) {
                            this.onInscricaoRowClick(item);
                            this.enableInscricaoUpdateDelete({ data: item } as TableRowSelectEvent);
                            this.abrirDialogCancelarSecundaria(idx);
                        }
                    }
                }
            }
        } as TableMetadata;
    });

    inscricaoSecundariaColumnOrder = ['dataInscricao', 'ufDestino', 'modoInscricao', 'dataCancelamento'];

    private situacaoOriginal: HistoricoRequest[] = [];
    private secundariasOriginal: HistoricoRequest[] = [];

    private situacaoMedicoSnapshot: SituacaoMedicoRow[] = [];
    private historicoSecundariaSnapshot: InscricaoSecundariaRow[] = [];

    loadingSituacao = signal(false);
    loadingSecundaria = signal(false);
    erroSituacao?: string;
    erroSecundaria?: string;

    private readonly fb = inject(FormBuilder);
    readonly novaInscricaoForm = this.fb.group({
        dataInscricao: ['', [this.dataMaiorQueHojeValidator()]],
        destinoUF: ['']
    });
    readonly cancelarInscricaoForm = this.fb.group({
        dataCancelamento: ['', [this.dataMaiorQueHojeValidator()]]
    });
    readonly excluirSituacaoForm = this.fb.group({
        dataCancelamento: ['', [this.dataMaiorQueHojeValidator()]]
    });
    readonly cancelarSecundariaForm = this.fb.group({
        dataCancelamento: ['', [this.dataMaiorQueHojeValidator()]]
    });

    showNovaInscricaoDialog = false;
    showExcluirSituacaoDialog = false;
    situacaoSelecionadaIndex: number | null = null;
    showCancelarSecundariaDialog = false;
    secundariaSelecionadaIndex: number | null = null;

    private cancelamentoPendente: MedicoHistoricoCancelamentoRequest | null = null;
    saving = signal(false);
    savingNovaInscricao = signal(false);
    savingCancelarSecundaria = signal(false);
    selectionSituacao: SituacaoMedicoRow | null = null;
    selectionInscricao: InscricaoSecundariaRow | null = null;
    selectionSituacaoIndex: number = -1;
    selectionInscricaoIndex: number = -1;

    private readonly ACAO_ADICIONAR_TEXT = 'Adicionar';
    private readonly ACAO_CANCELAR_INSCRICAO_TEXT = 'Cancelar Inscrição';
    private readonly ACAO_MAIS_INFORMACOES_TEXT = 'Mais Informações';
    showMaisInformacoesDialog = false;
    detalhesMaisInformacoes: MaisInformacaoItem[] = [];

    viewModeAtual = signal<'table' | 'card'>('table');

    /** A timeline é o padrão do histórico; tabela e cards continuam a um clique. */
    timelineAtiva = signal(true);

    acoesSituacao: MenuItem[] = [];
    acoesInscricao: MenuItem[] = [];

    get acoesAtivas(): MenuItem[] {
        return this.selectedTab === 'SITUACAO' ? this.acoesSituacao : this.acoesInscricao;
    }

    hashableOptionsTab: Map<string, TabContainerOptionType> = new Map([
        [
            'situacao',
            {
                label: 'Situação do Médico',
                icon: 'pi pi-id-card',
                value: 'SITUACAO'
            }
        ],
        [
            'inscricao',
            {
                label: 'Inscrição Secundária',
                value: 'SECUNDARIA'
            }
        ]
    ]);
    selectedTab: string = 'SITUACAO';
    optionsTab = Array.from(this.hashableOptionsTab.values());
    constructor(
        private historicoService: HistoricoFeatureService,
        private messageService: MessageService
    ) {
        this.acoesSituacao = this.criarAcoesSituacao();
        this.acoesInscricao = this.criarAcoesInscricao();
    }

    ngOnInit(): void {}

    private criarAcoesSituacao(): MenuItem[] {
        return [
            {
                label: this.ACAO_ADICIONAR_TEXT,
                icon: 'pi pi-plus-circle',
                styleClass: 'bg-green-500',
                permission: this.roleHistoricoInscricaoSecundaria,
                command: () => this.abrirDialogNovaInscricao()
            },
            {
                label: this.ACAO_CANCELAR_INSCRICAO_TEXT,
                icon: 'pi pi-ban',
                styleClass: 'bg-red-500',
                acaoHistorico: 'cancelar',
                disabled: true,
                permission: this.roleHistoricoCancelar,
                command: () => this.abrirDialogExcluirSituacao()
            },
            {
                label: this.ACAO_MAIS_INFORMACOES_TEXT,
                icon: 'pi pi-info-circle',
                styleClass: 'bg-cyan-600',
                acaoHistorico: 'mais-informacoes',
                disabled: true,
                command: () => this.abrirMaisInformacoesSituacao()
            }
        ];
    }

    private criarAcoesInscricao(): MenuItem[] {
        return [
            {
                label: this.ACAO_ADICIONAR_TEXT,
                icon: 'pi pi-plus-circle',
                styleClass: 'bg-green-500',
                permission: this.roleHistoricoInscricaoSecundaria,
                command: () => this.abrirDialogNovaInscricao()
            },
            {
                label: this.ACAO_CANCELAR_INSCRICAO_TEXT,
                icon: 'pi pi-ban',
                styleClass: 'bg-red-500',
                acaoHistorico: 'cancelar',
                disabled: true,
                permission: this.roleHistoricoCancelar,
                command: () => {
                    if (this.selectionInscricaoIndex >= 0) {
                        this.abrirDialogCancelarSecundaria(this.selectionInscricaoIndex);
                    }
                }
            },
            {
                label: this.ACAO_MAIS_INFORMACOES_TEXT,
                icon: 'pi pi-info-circle',
                styleClass: 'bg-cyan-600',
                acaoHistorico: 'mais-informacoes',
                disabled: true,
                command: () => this.abrirMaisInformacoesInscricao()
            }
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa']) {
            const p = this.pessoa();
            if (p && p.id !== undefined && p.id !== null && !!p.uf) {
                this.carregarSituacao(p);
                this.carregarSecundarias(p);
            } else {
                this.situacaoMedico.set([]);
                this.historicoSecundaria.set([]);
                this.resetarSelecaoSituacao();
                this.resetarSelecaoInscricao();
            }
        }
    }

    private carregarSituacao(pessoa: Pessoa): void {
        const params: Pessoa = { id: pessoa.id, uf: pessoa.uf };
        this.loadingSituacao.set(true);
        this.erroSituacao = undefined;
        this.historicoService
            .buscar(params)
            .pipe(
                finalize(() => {
                    this.loadingSituacao.set(false);
                })
            )
            .subscribe({
                next: (resp: HistoricoRequest[]) => {
                    if (resp && Array.isArray(resp) && resp.length) {
                        const situacaoOrdenada = this.ordenarPorData(resp);
                        this.situacaoOriginal = situacaoOrdenada;
                        this.situacaoMedico.set(situacaoOrdenada.map((h) => this.mapSituacao(h)));
                    } else {
                        this.situacaoOriginal = [];
                        this.situacaoMedico.set([]);
                    }
                    this.resetarSelecaoSituacao();
                },
                error: (err) => {
                    this.logger.error('Erro ao buscar histórico (situação do médico)', err);
                    this.situacaoMedico.set([]);
                    this.resetarSelecaoSituacao();
                    // Mensagem fica inline no card: o toast desaparece e o usuário ficaria sem saber
                    // por que a lista esvaziou. O toast do erro em si é do interceptor global.
                    this.erroSituacao = 'Falha ao carregar situação do médico.';
                }
            });
    }

    private carregarSecundarias(pessoa: Pessoa): void {
        const params: Pessoa = { id: pessoa.id, uf: pessoa.uf };
        this.loadingSecundaria.set(true);
        this.erroSecundaria = undefined;
        this.historicoService
            .buscarSecundarias(params)
            .pipe(
                finalize(() => {
                    this.loadingSecundaria.set(false);
                })
            )
            .subscribe({
                next: (resp: HistoricoRequest[]) => {
                    if (resp && Array.isArray(resp) && resp.length) {
                        const secundariasOrdenadas = this.ordenarPorData(resp);
                        this.secundariasOriginal = secundariasOrdenadas;
                        this.historicoSecundaria.set(secundariasOrdenadas.map((h) => this.mapSecundaria(h)));
                    } else {
                        this.secundariasOriginal = [];
                        this.historicoSecundaria.set([]);
                    }
                    this.resetarSelecaoInscricao();
                },
                error: (err) => {
                    this.logger.error('Erro ao buscar histórico (secundárias)', err);
                    this.historicoSecundaria.set([]);
                    this.resetarSelecaoInscricao();
                    // Idem: inline no card, sem toast local.
                    this.erroSecundaria = 'Falha ao carregar histórico de inscrições secundárias.';
                }
            });
    }

    private mapSituacao(h: HistoricoRequest): SituacaoMedicoRow {
        return {
            dataReferencia: this.formatarData(h.dtSituacao),
            descricao: h.dsModoInscricao ?? '',
            origemDestino: h.ufOrigemDestino ?? '',
            dataCancelamento: this.formatarData(h.dtCancelamento),
            motivoCancelamento: h.cancelamento ?? '',
            idModoInscricao: h.idModoInscricao,
            ufSgUf: h.ufSgUf ?? ''
        };
    }

    private mapSecundaria(h: HistoricoRequest): InscricaoSecundariaRow {
        return {
            dataInscricao: this.formatarData(h.dtSituacao),
            ufDestino: h.ufOrigemDestino ?? '',
            modoInscricao: h.dsModoInscricao ?? '',
            dataCancelamento: this.formatarData(h.dtCancelamento),
            idModoInscricao: h.idModoInscricao,
            ufSgUf: h.ufSgUf ?? ''
        };
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return String(valor);
        }
    }

    /** Mais recente primeiro; usado tanto para a situação quanto para as secundárias na carga. */
    private ordenarPorData(lista: HistoricoRequest[]): HistoricoRequest[] {
        return [...lista].sort((a, b) => dataParaTimestamp(b.dtSituacao) - dataParaTimestamp(a.dtSituacao));
    }

    criarNovaInscricao(): void {
        if (this.novaInscricaoForm.invalid) {
            this.novaInscricaoForm.markAllAsTouched();
            return;
        }
        const { dataInscricao, destinoUF } = this.novaInscricaoForm.value as { dataInscricao?: string; destinoUF?: string };
        if (!dataInscricao || !destinoUF) {
            this.messageService.add({ severity: 'warn', summary: 'Formulário Inválido', detail: 'Informe a data da inscrição e a UF de destino.' });
            return;
        }

        const pessoa = this.pessoa?.();
        if (!pessoa || pessoa.id == null || !pessoa.uf) {
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Pessoa não informada.' });
            return;
        }

        const body = {
            dtInscricao: this.ddmmaaToIso(dataInscricao),
            ufDestino: destinoUF
        };

        this.savingNovaInscricao.set(true);
        this.historicoService
            .adicionarInscricaoSecundaria({ id: pessoa.id, uf: pessoa.uf }, body)
            .pipe(
                finalize(() => {
                    this.savingNovaInscricao.set(false);
                })
            )
            .subscribe({
                next: (criado) => {
                    if (!criado) {
                        this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Não foi possível criar a inscrição secundária (conflito/dados inválidos).' });
                        return;
                    }
                    this.messageService.add({ severity: 'success', summary: 'Operação realizada', detail: 'Inscrição secundária adicionada com sucesso.', life: 3000 });
                    this.fecharDialogNovaInscricao();
                    this.carregarSecundarias({ id: pessoa.id, uf: pessoa.uf });
                },
                error: (err) => {
                    // Sem toast local: o interceptor global já exibiu a mensagem do backend, mais
                    // específica que este texto fixo. O diálogo continua aberto para correção.
                    this.logger.error('Erro ao criar inscrição secundária:', err);
                }
            });
    }

    abrirDialogNovaInscricao(): void {
        this.novaInscricaoForm.reset();
        this.logger.info('Shows novaInscricaoDialog');
        this.showNovaInscricaoDialog = true;
    }

    fecharDialogNovaInscricao(): void {
        this.showNovaInscricaoDialog = false;
        this.novaInscricaoForm.reset();
    }

    abrirDialogExcluirSituacao(): void {
        const currentSituacaoSelection = this.selectionSituacao;
        if (!currentSituacaoSelection) return;
        // Quando a seleção já traz um índice exato (timeline, ou linha de tabela já resolvida),
        // usa-o direto — recalcular por `findIndex`/`isEqualSituacaoMedico` arrisca colapsar
        // duplicatas idênticas em data/UF/descrição na primeira ocorrência da lista.
        const selectedSituacaoIndex = this.selectionSituacaoIndex >= 0 ? this.selectionSituacaoIndex : this.situacaoMedico().findIndex((situacaoMedico) => this.isEqualSituacaoMedico(situacaoMedico, currentSituacaoSelection));
        this.abrirDialogExcluirSituacaoWithIndex(selectedSituacaoIndex);
    }

    abrirDialogExcluirSituacaoWithIndex(index: number): void {
        this.situacaoSelecionadaIndex = index;
        const hoje = this.hojeBR();
        this.excluirSituacaoForm.reset();
        this.excluirSituacaoForm.get('dataCancelamento')?.setValue(hoje);
        this.showExcluirSituacaoDialog = true;
    }

    fecharDialogExcluirSituacao(): void {
        this.showExcluirSituacaoDialog = false;
        this.situacaoSelecionadaIndex = null;
        this.excluirSituacaoForm.reset();
    }

    confirmarExclusaoSituacao(): void {
        if (this.excluirSituacaoForm.invalid) {
            this.excluirSituacaoForm.markAllAsTouched();
            return;
        }
        const data = this.excluirSituacaoForm.get('dataCancelamento')?.value as string | null;
        if (!data || this.situacaoSelecionadaIndex === null) return;
        const idx = this.situacaoSelecionadaIndex;
        if (idx < 0) return;

        const original = this.situacaoOriginal[idx];
        if (!original) {
            this.fecharDialogExcluirSituacao();
            return;
        }

        const dtIso = this.ddmmaaToIso(data);
        this.cancelamentoPendente = {
            idSolicitante: original.idSolicitante,
            uf: original.ufSgUf,
            idModoInscricao: original.idModoInscricao,
            dtSituacao: original.dtSituacao,
            dtCancelamento: dtIso
        };

        const linhas = this.situacaoMedico();
        const copia = { ...linhas[idx] };
        copia.dataCancelamento = data;
        this.situacaoMedico.set([...linhas.slice(0, idx), copia, ...linhas.slice(idx + 1)]);

        this.fecharDialogExcluirSituacao();
    }

    abrirDialogCancelarSecundaria(index: number): void {
        this.secundariaSelecionadaIndex = index;
        const hoje = this.hojeBR();
        this.cancelarSecundariaForm.reset();
        this.cancelarSecundariaForm.get('dataCancelamento')?.setValue(hoje);
        this.showCancelarSecundariaDialog = true;
    }

    fecharDialogCancelarSecundaria(): void {
        this.showCancelarSecundariaDialog = false;
        this.secundariaSelecionadaIndex = null;
        this.cancelarSecundariaForm.reset();
    }

    confirmarCancelamentoSecundaria(): void {
        if (this.cancelarSecundariaForm.invalid) {
            this.cancelarSecundariaForm.markAllAsTouched();
            return;
        }
        const data = this.cancelarSecundariaForm.get('dataCancelamento')?.value as string | null;
        if (!data || this.secundariaSelecionadaIndex === null) return;
        const idx = this.secundariaSelecionadaIndex;
        if (idx < 0) return;

        const original = this.secundariasOriginal[idx];
        if (!original) {
            this.fecharDialogCancelarSecundaria();
            return;
        }

        const dtIso = this.ddmmaaToIso(data);
        const payload: MedicoHistoricoCancelamentoRequest = {
            idSolicitante: original.idSolicitante,
            uf: original.ufSgUf,
            idModoInscricao: original.idModoInscricao,
            dtSituacao: original.dtSituacao,
            dtCancelamento: dtIso
        };

        this.savingCancelarSecundaria.set(true);
        this.historicoService
            .atualizarCancelamento(payload)
            .pipe(
                finalize(() => {
                    this.savingCancelarSecundaria.set(false);
                })
            )
            .subscribe({
                next: (ok) => {
                    this.messageService.add({ severity: 'success', summary: 'Operação realizada', detail: 'Inscrição secundária cancelada com sucesso.', life: 3000 });
                    this.fecharDialogCancelarSecundaria();
                    const p = this.pessoa();
                    if (p && p.id != null && p.uf) {
                        this.carregarSecundarias(p);
                    }
                },
                error: (err) => {
                    // Sem toast local — ver acima.
                    this.logger.error('Falha ao cancelar inscrição secundária:', err);
                }
            });
    }

    habilitarEdicao(): void {
        this.situacaoMedicoSnapshot = this.situacaoMedico().map((r) => ({ ...r }));
        this.historicoSecundariaSnapshot = this.historicoSecundaria().map((r) => ({ ...r }));
    }

    salvarEdicao(): void {
        const payload = this.cancelamentoPendente;
        if (payload) {
            this.saving.set(true);
            this.historicoService
                .atualizarCancelamento(payload)
                .pipe(
                    finalize(() => {
                        this.saving.set(false);
                    })
                )
                .subscribe({
                    next: (ok) => {
                        this.messageService.add({ severity: 'success', summary: 'Operação realizada', detail: 'Histórico salvo com sucesso.', life: 3000 });
                        this.aposSalvar();
                        const p = this.pessoa();
                        if (p && p.id != null && p.uf) {
                            this.carregarSituacao(p);
                        }
                    },
                    error: (err) => {
                        // Sem toast local — ver acima.
                        this.logger.error('Falha ao atualizar cancelamento:', err);
                    }
                });
            return;
        }

        this.aposSalvar();
    }

    private aposSalvar(): void {
        this.situacaoMedicoSnapshot = [];
        this.historicoSecundariaSnapshot = [];
        this.cancelamentoPendente = null;
        this.resetarSelecaoSituacao();
        this.resetarSelecaoInscricao();
        this.showNovaInscricaoDialog = false;
        this.showExcluirSituacaoDialog = false;
        this.fecharDialogMaisInformacoes();
    }

    cancelarEdicao(): void {
        this.situacaoMedico.set(this.situacaoMedicoSnapshot.map((r) => ({ ...r })));
        this.historicoSecundaria.set(this.historicoSecundariaSnapshot.map((r) => ({ ...r })));
        this.resetarSelecaoSituacao();
        this.resetarSelecaoInscricao();
        this.situacaoMedicoSnapshot = [];
        this.historicoSecundariaSnapshot = [];
        this.cancelamentoPendente = null;
        this.fecharDialogNovaInscricao();
        this.fecharDialogExcluirSituacao();
        this.fecharDialogMaisInformacoes();
    }

    private hojeBR(): string {
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    }

    private ddmmaaToIso(v: string): string {
        const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!m) return v;
        const [, dd, mm, yyyy] = m;
        return `${yyyy}-${mm}-${dd}`;
    }

    enableInscricaoUpdateDelete(event: TableRowSelectEvent) {
        this.selectionInscricaoIndex = this.historicoSecundaria().findIndex((hist) => this.isEqualHistoricoSecundaria(hist, event.data));
    }

    enableSituacaoUpdateDelete(event: TableRowSelectEvent) {
        this.selectionSituacaoIndex = this.situacaoMedico().findIndex((situacao) => this.isEqualSituacaoMedico(situacao, event.data));
    }

    private isEqualHistoricoSecundaria(hist1: InscricaoSecundariaRow, hist2: InscricaoSecundariaRow): boolean {
        return hist1.dataCancelamento === hist2.dataCancelamento && hist1.dataInscricao === hist2.dataInscricao && hist1.modoInscricao === hist2.modoInscricao;
    }

    private isEqualSituacaoMedico(sit1: SituacaoMedicoRow, sit2: SituacaoMedicoRow): boolean {
        return sit1.dataCancelamento === sit2.dataCancelamento && sit1.descricao === sit2.descricao && sit1.dataReferencia === sit2.dataReferencia && sit1.origemDestino === sit2.origemDestino;
    }

    onSituacaoRowClick(row: SituacaoMedicoRow | null): void {
        if (!row) {
            this.resetarSelecaoSituacao();
            return;
        }

        this.selectionSituacao = row;
        this.enableSituacaoUpdateDelete({ data: row } as TableRowSelectEvent);
        this.atualizarAcoesPorSelecao('situacao', true);
    }

    onInscricaoRowClick(row: InscricaoSecundariaRow | null): void {
        if (!row) {
            this.resetarSelecaoInscricao();
            return;
        }

        this.selectionInscricao = row;
        this.enableInscricaoUpdateDelete({ data: row } as TableRowSelectEvent);
        this.atualizarAcoesPorSelecao('inscricao', true);
    }

    onEventoSituacaoSelecionado(indice: number | null): void {
        const linha = indice === null ? null : (this.situacaoMedico()[indice] ?? null);
        this.onSituacaoRowClick(linha);
        // `onSituacaoRowClick` resolve a seleção por `findIndex` com um comparador que casa por
        // campos de exibição — duplicatas (mesma data/UF/descrição) colapsam na primeira. A
        // timeline já sabe o índice exato, então ele prevalece sobre o que o findIndex achou.
        if (indice !== null && linha !== null) {
            this.selectionSituacaoIndex = indice;
        }
    }

    onEventoInscricaoSelecionado(indice: number | null): void {
        const linha = indice === null ? null : (this.historicoSecundaria()[indice] ?? null);
        this.onInscricaoRowClick(linha);
        if (indice !== null && linha !== null) {
            this.selectionInscricaoIndex = indice;
        }
    }

    get indiceEventoSituacao(): number | null {
        return this.selectionSituacaoIndex >= 0 ? this.selectionSituacaoIndex : null;
    }

    get indiceEventoInscricao(): number | null {
        return this.selectionInscricaoIndex >= 0 ? this.selectionInscricaoIndex : null;
    }

    abrirMaisInformacoesSituacao(): void {
        if (!this.selectionSituacao) return;
        this.detalhesMaisInformacoes = [
            { label: 'Data Referência', valor: this.selectionSituacao.dataReferencia || '-' },
            { label: 'Descrição', valor: this.selectionSituacao.descricao || '-' },
            { label: 'Origem/Destino', valor: this.selectionSituacao.origemDestino || '-' },
            { label: 'Data Cancelamento', valor: this.selectionSituacao.dataCancelamento || '-' },
            { label: 'Motivo Cancelamento', valor: this.selectionSituacao.motivoCancelamento || '-' }
        ];
        // Na timeline (padrão da aba) a tabela nem chega a montar — sem viewChild, o diálogo
        // próprio é quem mostra os detalhes. Com a tabela montada, mantém o comportamento
        // pré-existente de expandir a linha selecionada.
        const tabela = this.tabelaSituacao();
        if (tabela) {
            tabela.toggleSelectedRowDetails();
        } else {
            this.showMaisInformacoesDialog = true;
        }
    }

    abrirMaisInformacoesInscricao(): void {
        if (!this.selectionInscricao) return;
        this.detalhesMaisInformacoes = [
            { label: 'Data Inscrição', valor: this.selectionInscricao.dataInscricao || '-' },
            { label: 'UF Destino', valor: this.selectionInscricao.ufDestino || '-' },
            { label: 'Modo Inscrição', valor: this.selectionInscricao.modoInscricao || '-' },
            { label: 'Data Cancelamento', valor: this.selectionInscricao.dataCancelamento || '-' }
        ];
        const tabela = this.tabelaInscricao();
        if (tabela) {
            tabela.toggleSelectedRowDetails();
        } else {
            this.showMaisInformacoesDialog = true;
        }
    }

    fecharDialogMaisInformacoes(): void {
        this.showMaisInformacoesDialog = false;
        this.detalhesMaisInformacoes = [];
    }

    private atualizarAcoesPorSelecao(tabKey: 'situacao' | 'inscricao', hasSelection: boolean): void {
        const source = tabKey === 'situacao' ? this.acoesSituacao : this.acoesInscricao;
        const updated = source.map((item) => {
            const acaoHistorico = (item as MenuItem & { acaoHistorico?: string }).acaoHistorico;
            if (acaoHistorico === 'cancelar' || acaoHistorico === 'mais-informacoes') {
                return { ...item, disabled: !hasSelection };
            }
            return item;
        });
        if (tabKey === 'situacao') {
            this.acoesSituacao = updated;
        } else {
            this.acoesInscricao = updated;
        }
    }

    private resetarSelecaoSituacao(): void {
        this.selectionSituacao = null;
        this.selectionSituacaoIndex = -1;
        this.atualizarAcoesPorSelecao('situacao', false);
    }

    private resetarSelecaoInscricao(): void {
        this.selectionInscricao = null;
        this.selectionInscricaoIndex = -1;
        this.atualizarAcoesPorSelecao('inscricao', false);
    }

    onTabChanged(_event: { label: string; value: string }): void {}

    private dataMaiorQueHojeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            if (!DateUtils.isDataValida(valor)) {
                return { dataInvalida: true };
            }

            return FormularioValidatorUtil.dataMaiorQueHoje(valor) ? { dataMaiorQueHojeInvalida: true } : null;
        };
    }

    campoInvalido(form: FormGroup, nomeCampo: string): boolean {
        const campo = form.get(nomeCampo);
        return !!campo && campo.invalid && (campo.touched || campo.dirty);
    }

    mensagemErroCampo(form: FormGroup, nomeCampo: string): string | null {
        const campo = form.get(nomeCampo);
        if (!campo?.errors) return null;

        if (campo.errors['dataInvalida']) return 'Data inválida.';
        if (campo.errors['dataMaiorQueHojeInvalida']) return 'A data deve ser menor ou igual a hoje.';
        return 'Valor inválido.';
    }

    onTabChangedLegacy(_event: { label: string; value: string }) {
        // let changedActions: TabContainerActionType;
        // if (event.value === 'SITUACAO') {
        //     changedActions = {
        //             label: 'Adicionar situação',
        //             icon: 'pi pi-plus-circle',
        //             command: () => this.abrirDialogNovaInscricao(),
        //             type: 'button',
        //             disabled: false
        //     };
        // } else {
        //     changedActions = {
        //         label: 'Adicionar inscrição',
        //         icon: 'pi pi-plus-circle',
        //         command: () => this.abrirDialogNovaInscricao(),
        //         type: 'button',
        //         disabled: false
        //     };
        // }
        // this.customActions = { situacao: changedActions };
    }

    obterDescricaoExibicao(item: SituacaoMedicoRow): string {
        if (!item || !item.descricao) return 'N/A';
        return item.descricao.length > 70 ? item.descricao.substring(0, 70) + '...' : item.descricao;
    }

    obterModoInscricaoExibicao(item: InscricaoSecundariaRow): string {
        if (!item || !item.modoInscricao) return 'N/A';
        return item.modoInscricao.length > 70 ? item.modoInscricao.substring(0, 70) + '...' : item.modoInscricao;
    }
}
